import React from 'react';
import { Box, Button, Flex, IconButton, Select, Text, TextField } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { CountryCitySelector, FormField, MultiSelect } from 'shared/components';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { AddIcon, ClearIcon, SearchIcon } from 'shared/icons';

import { useCreateResumeContext } from '../create-resume.context';

type DesiredPositionProps = {
  renderSteps: (valid: boolean) => React.ReactNode;
};

const DesiredPositionView = ({ renderSteps }: DesiredPositionProps) => {
  const { desiredPosition } = useCreateResumeContext();

  return (
    <form>
      <Box>
        <Flex direction="column" gap="4" my="4">
          <FormField label="Назва посади" required>
            <AutocompleteV2.Root
              items={desiredPosition.positionAutocompleteOptions}
              viewModel={desiredPosition.positionVM}
              getOptionValue={(position) => position.id}
              getOptionLabel={(position) => position.name}
              color="indigo"
              indicator={<SearchIcon width={20} height={20} />}
              rotateIndicatorOnOpen={false}
              loading={desiredPosition.isPositionLoading}
              placeholder="Введіть назву посади"
              resetInputOnClose="never"
              onSearch={desiredPosition.onSearchPosition}
              onSelect={desiredPosition.onSelectPosition}
            />
          </FormField>

          <CountryCitySelector
            store={desiredPosition.location}
            countries={desiredPosition.countries}
            requiredCountry={true}
            requiredCity={true}
          />

          <Flex gap="4">
            <FormField label="Зарплата від" required>
              <TextField.Root
                size="3"
                color="indigo"
                inputMode="numeric"
                value={desiredPosition.salaryMin}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  desiredPosition.setSalaryMin(event.target.value);
                }}
              />
            </FormField>

            <FormField label="Зарплата до" required>
              <TextField.Root
                size="3"
                color="indigo"
                inputMode="numeric"
                value={desiredPosition.salaryMax}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  desiredPosition.setSalaryMax(event.target.value);
                }}
              />
            </FormField>
          </Flex>

          <Flex justify="end">
            <Box style={{ width: 'calc((100% - var(--space-4)) / 2)' }}>
              <FormField label="Валюта" required>
                <Select.Root
                  size="3"
                  value={desiredPosition.salaryCurrency}
                  onValueChange={(value) => {
                    desiredPosition.setSalaryCurrency(value);
                  }}
                >
                  <Select.Trigger color="indigo" placeholder="Виберіть валюту" />
                  <Select.Content color="indigo" position="popper">
                    {desiredPosition.currencies.map((currency) => (
                      <Select.Item key={currency.id} value={currency.id}>
                        {currency.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FormField>
            </Box>
          </Flex>

          <FormField label="Зайнятість" required>
            <MultiSelect.Root
              vm={desiredPosition.workTypes}
              placeholder="Виберіть зайнятість"
            >
              {desiredPosition.workTypes.results.map((workType) => (
                <MultiSelect.Option key={workType.id} value={workType}>
                  {workType.label}
                </MultiSelect.Option>
              ))}
            </MultiSelect.Root>
          </FormField>

          {desiredPosition.desiredCategories.map((desiredCategory) => (
            <Box
              key={desiredCategory.id}
              p="4"
              style={{ border: '1px solid var(--gray-a6)', borderRadius: 8 }}
            >
              <Flex direction="column" gap="4">
                <Flex justify="between" align="center">
                  <Text weight="medium">Категорія та спеціалізація</Text>
                  <IconButton
                    type="button"
                    variant="soft"
                    color="red"
                    onClick={() => {
                      desiredPosition.removeDesiredCategory(desiredCategory.id);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Flex>

                <FormField label="Категорія" required>
                  <Select.Root
                    size="3"
                    value={desiredCategory.categoryId}
                    onValueChange={(value) => {
                      desiredPosition.onDesiredCategoryChange(desiredCategory.id, value);
                    }}
                  >
                    <Select.Trigger color="indigo" placeholder="Оберіть категорію" />
                    <Select.Content color="indigo" position="popper">
                      {desiredPosition.categories.map((category) => (
                        <Select.Item key={category.id} value={String(category.id)}>
                          {category.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </FormField>

                <FormField label="Спеціалізація" required>
                  <MultiSelect.Root
                    vm={desiredCategory.specializations}
                    placeholder={
                      desiredCategory.categoryId
                        ? 'Оберіть спеціалізацію'
                        : 'Спочатку оберіть категорію'
                    }
                  >
                    {desiredCategory.filteredOptions.map((subcategory) => (
                      <MultiSelect.Option key={subcategory.id} value={subcategory}>
                        {subcategory.name}
                      </MultiSelect.Option>
                    ))}
                  </MultiSelect.Root>
                </FormField>

                {desiredCategory.isLoading ? (
                  <Text size="1" color="gray">
                    Завантаження спеціалізацій...
                  </Text>
                ) : null}
              </Flex>
            </Box>
          ))}

          <Button
            type="button"
            variant="outline"
            disabled={desiredPosition.desiredCategories.length >= 3}
            onClick={() => {
              desiredPosition.addDesiredCategory();
            }}
          >
            Додати ще <AddIcon />
          </Button>
        </Flex>
      </Box>

      <Flex mt="4">{renderSteps(desiredPosition.valid)}</Flex>
    </form>
  );
};

export const DesiredPosition = observer(DesiredPositionView);
